import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { billing, subscriptions, isApiError } from '@repletoryreach/sdk-api';
import { Card, Group, Stack, Text, Title, Button, Badge, Progress } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, Switch, Radio, RadioGroup } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { formatMinor, formatDate } from '../../../lib/format';
import { useCredits } from '../../../lib/credits';
import { useI18n, type Translate } from '../../../i18n/I18nProvider';
import { useIsOrgAdmin } from '../../../app/components/RoleGate';
import { useAddCard } from './PaymentCard';

export const EXTRA_USE_KEY = ['billing', 'extra-use'];

type Mode = billing.ExtraUseMode;
type View = billing.ExtraUseView;
type Field = 'limit' | 'step' | 'topup' | 'below';

/** The form, in rand as typed. The service stores cents. */
interface Draft {
  enabled: boolean;
  mode: Mode;
  limit: number | '';
  step: number | '';
  topup: number | '';
  below: number | '';
}

const MODES: Mode[] = ['steps', 'upfront', 'month_end'];
/** Upfront: "top up when below" when the business has not chosen one, as the service defaults it (R50). */
const DEFAULT_BELOW_MINOR = 5000;

const toMajor = (minor: number | null | undefined): number | '' => (minor == null ? '' : Math.round(minor) / 100);
const toMinor = (major: number | ''): number | null => (major === '' ? null : Math.round(major * 100));

function draftFrom(v: View): Draft {
  return {
    enabled: v.enabled,
    mode: v.mode ?? 'steps',
    limit: toMajor(v.limit_minor),
    step: toMajor(v.step_minor),
    topup: toMajor(v.topup_minor),
    below: toMajor(v.topup_below_minor ?? DEFAULT_BELOW_MINOR)
  };
}

/**
 * What is wrong with the form, field by field, in the platform's bounds. Empty when it can be saved. A business
 * switching extra use off needs nothing else filled in.
 */
export function draftErrors(d: Draft, bounds: billing.ExtraUseBounds, t: Translate, currency: string): Partial<Record<Field, string>> {
  const out: Partial<Record<Field, string>> = {};
  if (!d.enabled) return out;
  const money = (minor: number) => formatMinor(minor, currency);
  const limit = toMinor(d.limit);
  if (limit == null) out.limit = t('billing.extra.error.limitRequired');
  else if (limit < bounds.min_limit_minor || limit > bounds.max_limit_minor) {
    out.limit = t('billing.extra.error.limitRange', { min: money(bounds.min_limit_minor), max: money(bounds.max_limit_minor) });
  }
  const amount = (field: 'step' | 'topup', value: number | '') => {
    const minor = toMinor(value);
    if (minor == null) out[field] = t(`billing.extra.error.${field}Required`);
    else if (minor < bounds.min_step_minor) out[field] = t('billing.extra.error.amountMin', { min: money(bounds.min_step_minor) });
    else if (limit != null && minor > limit) out[field] = t('billing.extra.error.amountOverLimit');
  };
  if (d.mode === 'steps') amount('step', d.step);
  if (d.mode === 'upfront') {
    amount('topup', d.topup);
    const below = toMinor(d.below);
    if (below != null && below < 0) out.below = t('billing.extra.error.belowNegative');
  }
  return out;
}

/** The body for `PUT /v1/billing/extra-use`: off is `{ enabled: false }` alone, and each way of paying sends its own amounts. */
export function requestFrom(d: Draft): billing.ExtraUseUpdate {
  if (!d.enabled) return { enabled: false };
  return {
    enabled: true,
    mode: d.mode,
    limit_minor: toMinor(d.limit),
    step_minor: d.mode === 'steps' ? toMinor(d.step) : null,
    topup_minor: d.mode === 'upfront' ? toMinor(d.topup) : null,
    topup_below_minor: d.mode === 'upfront' ? toMinor(d.below) : null
  };
}

const FIELD_OF: Record<string, Field> = { limit_minor: 'limit', step_minor: 'step', topup_minor: 'topup', topup_below_minor: 'below' };
const REFUSALS = new Set(['EXTRA_USE_NOT_ON_PLAN', 'EXTRA_USE_NO_CARD', 'EXTRA_USE_NOT_OFFERED', 'EXTRA_USE_BLOCKED', 'EXTRA_USE_PAST_DUE']);

/** "Growth and Scale", from the plans on sale that include extra use, in the console's language. */
function planList(plans: subscriptions.Plan[], locale: string): string | null {
  const names = plans
    .filter((p) => p.monthly_amount_minor > 0 && billing.includesExtraUse(p.limits))
    .sort((a, b) => a.monthly_amount_minor - b.monthly_amount_minor)
    .map((p) => p.name);
  if (names.length === 0) return null;
  try {
    return new Intl.ListFormat(`${locale}-ZA`, { type: 'conjunction' }).format(names);
  } catch {
    return names.join(', ');
  }
}

/**
 * Extra use (owner, 30 October 2026): on Growth and Scale, with a saved card, everything keeps working after the
 * credit runs out, at the plan's prices, up to a monthly limit the business sets, and it chooses how that is paid
 * (in steps, upfront as credit, or on the 1st with the plan). While it is on the plan reads "Growth+" or "Scale+".
 *
 * Hidden where the platform does not offer it. A plan without it says which plans have it and opens the plan
 * chooser; no saved card asks for one. Owners and admins change it; everybody else reads it.
 */
export function ExtraUseCard({ byInvoice = false }: { byInvoice?: boolean }) {
  const { t, locale } = useI18n();
  const qc = useQueryClient();
  const isAdmin = useIsOrgAdmin();
  const addCard = useAddCard();
  const [, setParams] = useSearchParams();

  const viewQ = useQuery({ queryKey: EXTRA_USE_KEY, queryFn: () => billing.getExtraUse(), retry: false });
  const plansQ = useQuery({ queryKey: ['plans'], queryFn: () => subscriptions.listPlans(), retry: false });
  const view = viewQ.data?.data;
  const plans = useMemo(() => plansQ.data?.data ?? [], [plansQ.data]);
  const currency = view?.currency ?? 'ZAR';
  const money = (minor: number) => formatMinor(minor, currency);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [seeded, setSeeded] = useState(false);
  const [tried, setTried] = useState(false);
  const [serverField, setServerField] = useState<{ field: Field; message: string } | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  useEffect(() => {
    if (!view || seeded) return;
    setDraft(draftFrom(view));
    setSeeded(true);
  }, [view, seeded]);

  const save = useMutation({
    mutationFn: (req: billing.ExtraUseUpdate) => billing.updateExtraUse(req),
    onMutate: () => { setServerField(null); setRefusal(null); },
    onSuccess: (res) => {
      qc.setQueryData(EXTRA_USE_KEY, res);
      qc.invalidateQueries({ queryKey: ['wallet'] });
      qc.invalidateQueries({ queryKey: ['subscription'] });
      setSeeded(false);
      setTried(false);
      notifySuccess(res.data.enabled ? t('billing.extra.saved.on') : t('billing.extra.saved.off'), t('billing.extra.title'));
    },
    onError: (e) => {
      if (isApiError(e)) {
        const body = e.body as { error?: { field?: string } } | null;
        const field = body?.error?.field ? FIELD_OF[body.error.field] : undefined;
        if (e.code === 'VALIDATION' && field) { setServerField({ field, message: e.message }); return; }
        if (REFUSALS.has(e.code)) { setRefusal(t(`billing.extra.error.${e.code}`)); qc.invalidateQueries({ queryKey: EXTRA_USE_KEY }); return; }
        if (e.status === 403) { setRefusal(t('billing.extra.error.forbidden')); return; }
      }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('billing.extra.title'));
    }
  });

  if (!view || !draft) return null;
  // Not offered by the platform: nothing to show, unless it was on and something is still owed this month.
  if (!view.offered && !view.enabled) return null;

  const offeredOn = planList(plans, locale);
  const extraPlan = plans
    .filter((p) => p.monthly_amount_minor > 0 && billing.includesExtraUse(p.limits))
    .sort((a, b) => a.monthly_amount_minor - b.monthly_amount_minor)[0];
  // The plan chooser on this page opens on a plan named in the address, the way the public site's links do.
  const seePlans = () => setParams((prev) => { const q = new URLSearchParams(prev); q.set('plan', extraPlan?.code ?? ''); return q; });
  const plusName = view.plan_name ? `${view.plan_name}+` : null;

  const header = (
    <Group justify="space-between" align="flex-start" wrap="wrap" gap="sm">
      <Stack gap={2} style={{ flex: '1 1 280px' }}>
        <Group gap="sm" align="center">
          <Title order={4}>{t('billing.extra.title')}</Title>
          {view.active && plusName && <Badge size="sm" variant="light" color="blue" data-testid="extra-plus">{plusName}</Badge>}
          {view.enabled && !view.active && <Badge size="sm" variant="light" color="orange">{t('billing.extra.badge.paused')}</Badge>}
        </Group>
        <Text role="bodySmall" c="var(--rr-color-text-muted)">{t('billing.extra.description')}</Text>
      </Stack>
    </Group>
  );

  // A plan without extra use: which plans have it, and the way to them.
  if (!view.on_plan && !view.enabled) {
    return (
      <Card withBorder data-guide="billing-extra" data-testid="extra-use-card">
        <Stack gap="sm">
          {header}
          <Group justify="space-between" align="center" wrap="wrap" gap="sm" data-testid="extra-not-on-plan">
            <Text role="bodySmall">{offeredOn ? t('billing.extra.notOnPlan', { plans: offeredOn }) : t('billing.extra.notOnPlanDefault')}</Text>
            {!byInvoice && isAdmin && extraPlan && (
              <Button size="sm" variant="outline" onClick={seePlans}>{t('billing.plan.seePlans')}</Button>
            )}
          </Group>
        </Stack>
      </Card>
    );
  }

  // On the plan with no card: extra use is charged to a card, so it asks for one first.
  if (!view.has_card && !view.enabled) {
    return (
      <Card withBorder data-guide="billing-extra" data-testid="extra-use-card">
        <Stack gap="sm">
          {header}
          <Group justify="space-between" align="center" wrap="wrap" gap="sm" data-testid="extra-no-card">
            <Text role="bodySmall" style={{ flex: '1 1 280px' }}>{t('billing.extra.noCard')}</Text>
            {isAdmin && (
              <Button size="sm" loading={addCard.isPending} onClick={() => addCard.mutate()}>{t('billing.extra.addCard')}</Button>
            )}
          </Group>
        </Stack>
      </Card>
    );
  }

  const errors = draftErrors(draft, view.bounds, t, currency);
  if (serverField) errors[serverField.field] = serverField.message;
  const shown = (field: Field) => {
    const value = field === 'limit' ? draft.limit : field === 'step' ? draft.step : field === 'topup' ? draft.topup : draft.below;
    return tried || value !== '' || serverField?.field === field ? errors[field] : undefined;
  };
  const saved = draftFrom(view);
  const dirty = JSON.stringify(requestFrom(draft)) !== JSON.stringify(requestFrom(saved));
  const set = (patch: Partial<Draft>) => { setDraft((d) => (d ? { ...d, ...patch } : d)); setServerField(null); setRefusal(null); };
  const submit = () => {
    setTried(true);
    if (Object.keys(draftErrors(draft, view.bounds, t, currency)).length > 0) return;
    save.mutate(requestFrom(draft));
  };

  return (
    <Card withBorder data-guide="billing-extra" data-testid="extra-use-card">
      <Stack gap="md">
        {header}
        {view.enabled && view.paused_reason && <Paused view={view} isAdmin={isAdmin} onSeePlans={extraPlan && !byInvoice ? seePlans : undefined} />}
        {view.enabled && <ThisMonth view={view} money={money} />}
        {refusal && <Alert color="warning" role="alert" data-testid="extra-refusal">{refusal}</Alert>}

        {isAdmin ? (
          <Stack gap="md" data-testid="extra-form">
            <Switch
              checked={draft.enabled}
              onChange={(on) => set({ enabled: on })}
              label={t('billing.extra.switch')}
              description={view.card_label ? t('billing.extra.switchHint', { card: view.card_label }) : undefined}
            />
            {draft.enabled && (
              <Stack gap="md">
                <NumberInput
                  label={t('billing.extra.limit.label', { currency })}
                  value={draft.limit}
                  onChange={(v) => set({ limit: v })}
                  min={0}
                  step={100}
                  description={t('billing.extra.limit.hint', { min: money(view.bounds.min_limit_minor), max: money(view.bounds.max_limit_minor) })}
                  error={shown('limit')}
                  data-testid="extra-limit"
                />
                <RadioGroup label={t('billing.extra.mode.label')} value={draft.mode} onChange={(v) => set({ mode: v as Mode })}>
                  {MODES.map((m) => (
                    <div key={m} className="rr-billing__extra-mode">
                      <Radio value={m} label={t(`billing.extra.mode.${m}`)} description={t(`billing.extra.mode.${m}.hint`)} data-testid={`extra-mode-${m}`} />
                      {draft.mode === m && m === 'steps' && (
                        <div className="rr-billing__extra-fields">
                          <NumberInput
                            label={t('billing.extra.step.label', { currency })}
                            value={draft.step}
                            onChange={(v) => set({ step: v })}
                            min={0}
                            step={50}
                            description={t('billing.extra.step.hint', { min: money(view.bounds.min_step_minor) })}
                            error={shown('step')}
                            data-testid="extra-step"
                          />
                        </div>
                      )}
                      {draft.mode === m && m === 'upfront' && (
                        <div className="rr-billing__extra-fields">
                          <NumberInput
                            label={t('billing.extra.topup.label', { currency })}
                            value={draft.topup}
                            onChange={(v) => set({ topup: v })}
                            min={0}
                            step={50}
                            description={t('billing.extra.topup.hint', { min: money(view.bounds.min_step_minor) })}
                            error={shown('topup')}
                            data-testid="extra-topup"
                          />
                          <NumberInput
                            label={t('billing.extra.below.label')}
                            value={draft.below}
                            onChange={(v) => set({ below: v })}
                            min={0}
                            step={10}
                            description={t('billing.extra.below.hint')}
                            error={shown('below')}
                            data-testid="extra-below"
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </RadioGroup>
                <Text role="bodySmall" data-testid="extra-price-note">{t('billing.extra.priceNote')}</Text>
                <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.extra.consent')}</Text>
              </Stack>
            )}
            <Group gap="sm">
              <Button size="sm" onClick={submit} loading={save.isPending} disabled={!dirty} data-testid="extra-save">{t('action.save')}</Button>
            </Group>
          </Stack>
        ) : (
          <ReadOnly view={view} money={money} />
        )}
      </Stack>
    </Card>
  );
}

/** This month: what is used of the limit as a meter, what is owed and charged, and the next charge in words. */
function ThisMonth({ view, money }: { view: View; money: (minor: number) => string }) {
  const { t } = useI18n();
  const credits = useCredits();
  const limit = view.limit_minor ?? 0;
  const next = view.next_charge;
  const nextText = !next ? null
    : next.kind === 'step' ? t('billing.extra.next.step', { amount: money(next.at_minor ?? view.step_minor ?? 0) })
    : next.kind === 'topup' ? t('billing.extra.next.topup', { amount: money(view.topup_minor ?? 0), below: credits(next.at_minor ?? view.topup_below_minor ?? 0, view.currency) })
    : t('billing.extra.next.monthEnd', { date: formatDate(next.on ?? null) });
  return (
    <Stack gap={6} data-testid="extra-month">
      <Group justify="space-between" wrap="wrap" gap="xs">
        <Text role="bodySmall" fw={600}>{t('billing.extra.month.used', { used: money(view.used_minor), limit: money(limit) })}</Text>
        <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.extra.month.room', { room: money(view.room_minor) })}</Text>
      </Group>
      <Progress value={view.used_minor} max={Math.max(limit, 1)} size="sm" aria-label={t('billing.extra.month.meter')} />
      <Group gap="lg" wrap="wrap">
        <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.extra.month.owed', { amount: money(view.owed_minor) })}</Text>
        <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.extra.month.charged', { amount: money(view.charged_minor) })}</Text>
      </Group>
      {nextText && view.active && <Text role="caption" data-testid="extra-next">{nextText}</Text>}
    </Stack>
  );
}

/** Why extra use is paused, and what puts it right. */
function Paused({ view, isAdmin, onSeePlans }: { view: View; isAdmin: boolean; onSeePlans?: () => void }) {
  const { t } = useI18n();
  const addCard = useAddCard();
  const reason = view.paused_reason!;
  const cardAction = reason === 'declined' || reason === 'no_card' || reason === 'past_due';
  return (
    <Alert color="warning" role="alert" title={t('billing.extra.paused.title')} data-testid="extra-paused">
      <Stack gap={8}>
        <Text role="bodySmall">
          {t(`billing.extra.paused.${reason}`, t('billing.extra.paused.other'))}
          {reason === 'declined' && view.decline_reason ? ` ${t('billing.extra.paused.declineReason', { reason: view.decline_reason })}` : ''}
        </Text>
        {isAdmin && cardAction && (
          <div>
            <Button size="xs" loading={addCard.isPending} onClick={() => addCard.mutate()}>
              {reason === 'no_card' ? t('billing.extra.addCard') : reason === 'past_due' ? t('billing.extra.paused.payPlan') : t('billing.pastDue.action')}
            </Button>
          </div>
        )}
        {isAdmin && reason === 'not_on_plan' && onSeePlans && (
          <div><Button size="xs" variant="outline" onClick={onSeePlans}>{t('billing.plan.seePlans')}</Button></div>
        )}
      </Stack>
    </Alert>
  );
}

/** For a member who is not an owner or admin: the settings as words. */
function ReadOnly({ view, money }: { view: View; money: (minor: number) => string }) {
  const { t } = useI18n();
  const credits = useCredits();
  const how = !view.enabled || !view.mode ? null
    : view.mode === 'steps' ? t('billing.extra.read.steps', { amount: money(view.step_minor ?? 0) })
    : view.mode === 'upfront' ? t('billing.extra.read.upfront', { amount: money(view.topup_minor ?? 0), below: credits(view.topup_below_minor ?? DEFAULT_BELOW_MINOR, view.currency) })
    : t('billing.extra.read.monthEnd');
  return (
    <Stack gap={4} data-testid="extra-readonly">
      <Text role="bodySmall">{view.enabled ? t('billing.extra.read.on', { limit: money(view.limit_minor ?? 0) }) : t('billing.extra.read.off')}</Text>
      {how && <Text role="bodySmall">{how}</Text>}
      <Text role="caption" c="var(--rr-color-text-muted)">{t('billing.extra.read.who')}</Text>
    </Stack>
  );
}
